import { useCallback, useEffect, useState } from 'react'

import { Card } from '@taro/cards'
import { Stack, Text, applyTheme, getStoredTheme, DEFAULT_THEME } from '@taro/design'
import { startMotionGovernor } from '@taro/motion'
import { browserTimezone, haptic, initDataRaw, isInsideTelegram, ready } from '@taro/tg'

import { api, type Card as CardData, type Daily as DailyData, type Me } from './api'
import { Daily } from './screens/Daily'
import { Deck } from './screens/Deck'
import { Lab } from './screens/Lab'
import { Reading } from './screens/Reading'
import styles from './App.module.css'

type Tab = 'today' | 'reading' | 'deck' | 'lab'

type State =
  | { kind: 'loading' }
  | { kind: 'outside' }
  | { kind: 'ready'; me: Me; daily: DailyData; cards: CardData[] }
  | { kind: 'error'; message: string }

export function App() {
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [tab, setTab] = useState<Tab>('today')

  useEffect(() => {
    applyTheme(getStoredTheme() ?? DEFAULT_THEME)

    // Замер частоты кадров стартует здесь и живёт всё приложение: он один
    // на сессию и решает, дышит атмосфера или замирает.
    const stopGovernor = startMotionGovernor()

    const bg = getComputedStyle(document.documentElement).getPropertyValue('--c-bg-canvas').trim()
    ready({ backgroundColor: bg })

    if (!isInsideTelegram()) {
      setState({ kind: 'outside' })
      return stopGovernor
    }

    let cancelled = false
    const raw = initDataRaw()

    ;(async () => {
      try {
        const me = await api.me(raw)

        // Таймзона отправляется до карты дня и намеренно: карта назначается
        // на локальную дату, и с чужим часовым поясом человек получит
        // «сегодня» на день раньше или позже.
        const tz = browserTimezone()
        if (tz && me.timezone !== tz) {
          await api.setTimezone(raw, tz).catch(() => {})
        }

        const [daily, deck] = await Promise.all([api.daily(raw), api.deck(raw)])
        if (!cancelled) setState({ kind: 'ready', me, daily, cards: deck.cards })
      } catch (e) {
        if (!cancelled) setState({ kind: 'error', message: (e as Error).message })
      }
    })()

    return () => {
      cancelled = true
      stopGovernor()
    }
  }, [])

  // Баланс живёт в состоянии приложения, а не только внутри расклада:
  // он виден в шапке, и после списания шапка обязана обновиться сама.
  const setBalance = useCallback((balance: number) => {
    setState((s) => (s.kind === 'ready' ? { ...s, me: { ...s.me, balance } } : s))
  }, [])

  const reveal = useCallback(() => {
    api.revealDaily(initDataRaw()).catch(() => {
      // Отметка о перевороте — телеметрия, а не то, ради чего человек пришёл.
      // Её потеря не должна портить ему экран.
    })
  }, [])

  if (state.kind === 'loading') return <Screen><Loading /></Screen>
  if (state.kind === 'outside') return <Screen><Outside /></Screen>
  if (state.kind === 'error') return <Screen><Failed message={state.message} /></Screen>

  return (
    <div className={styles.app}>
      <header className={styles.header}>
        <Text variant="overline" tone="sacred">
          {state.me.isNew ? 'Первый раз' : 'С возвращением'}
        </Text>
        <div className={styles.title}>
          <Text variant="display-m" as="h1">
            {state.me.firstName}
          </Text>
          {/* Баланс рядом с именем, а не отдельным экраном: валюта должна
              быть на виду с первого дня, иначе её появление потом читается
              как «ввели платное». */}
          <span className={styles.balance} title="Внутренняя валюта">
            {state.me.balance}
          </span>
        </div>
      </header>

      <nav className={styles.tabs} aria-label="Разделы">
        <Tabs value={tab} onChange={setTab} />
      </nav>

      <main className={styles.content}>
        {tab === 'today' ? <Daily daily={state.daily} onReveal={reveal} /> : null}
        {tab === 'reading' ? (
          <Reading balance={state.me.balance} onBalance={setBalance} />
        ) : null}
        {tab === 'deck' ? <Deck cards={state.cards} /> : null}
        {tab === 'lab' ? <Lab /> : null}
      </main>
    </div>
  )
}

function Tabs({ value, onChange }: { value: Tab; onChange: (t: Tab) => void }) {
  const items: Array<[Tab, string]> = [
    ['today', 'Сегодня'],
    ['reading', 'Расклад'],
    ['deck', 'Колода'],
    // Временная вкладка: живёт, пока выбираем переходы. См. screens/Lab.tsx
    ['lab', 'Переходы'],
  ]

  return (
    <>
      {items.map(([id, label]) => (
        <button
          key={id}
          type="button"
          className={styles.tab}
          aria-current={value === id ? 'page' : undefined}
          onClick={() => {
            if (value !== id) haptic('select')
            onChange(id)
          }}
        >
          {label}
        </button>
      ))}
    </>
  )
}

function Screen({ children }: { children: React.ReactNode }) {
  return <main className={styles.center}>{children}</main>
}

function Loading() {
  return <Card back="/card-back.webp" label="Колода готовится" size="xl" loading />
}

/**
 * Экран вне Telegram.
 *
 * Ссылку открывают в браузере постоянно — из истории, из закладок,
 * с чужого сообщения. Белая страница здесь читается как «приложение
 * сломалось», а не как «ты не туда зашёл».
 */
function Outside() {
  return (
    <Stack gap={6} align="center">
      <Card back="/card-back.webp" label="Рубашка карты" size="xl" />

      <Stack gap={3} align="center">
        <Text variant="display-m" as="h1" style={{ textAlign: 'center' }}>
          Колода закрыта
        </Text>
        <Text variant="body-m" tone="tertiary" measure style={{ textAlign: 'center' }}>
          Приложение живёт внутри Telegram — оттуда оно знает, кто пришёл,
          и помнит все прошлые расклады.
        </Text>
      </Stack>

      <a className={styles.cta} href="https://t.me/TaroSecretCard_bot">
        Открыть в Telegram
      </a>
    </Stack>
  )
}

function Failed({ message }: { message: string }) {
  return (
    <Stack gap={4} align="center">
      <Text variant="title-m" as="h1">
        Карты не отвечают
      </Text>
      <Text variant="body-m" tone="tertiary" measure style={{ textAlign: 'center' }}>
        {message}
      </Text>
    </Stack>
  )
}
